import React from "react";

import { CheckFormState, ProductConfiguration, Unit } from "@/types";
import { getCheckFormStorage } from "@/utils/getCheckFormStorage";
import { getNumberValue } from "@/utils/getNumberValue";

import { useExperience } from "./useExperience";

interface CheckFormContextValue extends CheckFormState {
  countrySupported: boolean;
  getCheckFormSearchParams: () => string;
  hoursSet: (hours: string) => void;
  minutesSet: (minutes: string) => void;
  packagings: string[];
  packagingSet: (packaging: string | null) => void;
  products: string[];
  productSet: (product: string | null) => void;
  storages: string[];
  storageSet: (storage: string | null) => void;
  temperatureSet: (minutes: string) => void;
  unitSet: (unit: Unit) => void;
  valid: Boolean;
}

const CheckFormContext = React.createContext<CheckFormContextValue>({
  ...getCheckFormStorage().getState(null),
  countrySupported: false,
  getCheckFormSearchParams: () => "",
  hoursSet: () => {},
  minutesSet: () => {},
  packagings: [],
  packagingSet: () => {},
  products: [],
  productSet: () => {},
  storages: [],
  storageSet: () => {},
  temperatureSet: () => {},
  unitSet: () => {},
  valid: false
});

interface CheckFormProviderProps {
  children?: React.ReactNode;
  options: ProductConfiguration;
}

let hasSetStateFromStorage = false;
const checkFormStorage = getCheckFormStorage();

export const CheckFormProvider: React.FC<CheckFormProviderProps> = ({ children, options }) => {
  const { pathCountry } = useExperience();
  const countryActive = Object.values(options).find((o) => o.countryCode === pathCountry?.iso2);
  const countrySupported = !!countryActive;
  const productOptions = countryActive?.productsMap ? Object.values(countryActive.productsMap) : [];
  const products = productOptions.map((p) => p.productId);
  const [state, stateSet] = React.useState<CheckFormState>(
    typeof window !== "undefined" && hasSetStateFromStorage
      ? checkFormStorage.getState(countryActive?.productsMap || null)
      : checkFormStorage.getStateDefault()
  );
  const {
    hours,
    minutes,
    packaging: packagingInitial,
    product,
    storage: storageInitial,
    temperature,
    unit
  } = state;
  const productOption = productOptions?.find((p) => p.productId === state.product);
  const packagingOptions = productOption?.packagingMap
    ? Object.values(productOption.packagingMap)
    : [];
  const packagings = packagingOptions.map((p) => p.packagingId);
  const packagingOption = packagingOptions.find((p) => p.packagingId === packagingInitial);
  const packaging = packagingOption ? packagingInitial : null;
  const storages = packagingOption?.storageIds || [];
  const storage = storages.find((s) => s === storageInitial) ? storageInitial : null;
  const valid = !!(
    ((hours && getNumberValue(hours) !== 0) || (minutes && getNumberValue(minutes) !== 0)) &&
    packaging &&
    product &&
    storage &&
    temperature &&
    unit
  );

  const getCheckFormSearchParams = React.useCallback(() => {
    return new URLSearchParams({
      hours,
      minutes,
      packaging: packaging as string,
      product: product as string,
      storage: storage as string,
      temperature,
      unit: unit as string
    }).toString();
  }, [hours, minutes, packaging, product, storage, temperature, unit]);

  React.useEffect(() => {
    if (!hasSetStateFromStorage) {
      hasSetStateFromStorage = true;
      stateSet(checkFormStorage.getState(countryActive?.productsMap || null));
    }
  }, [countryActive?.productsMap]);

  const handleSetState = React.useCallback(
    (stateUpdate: Partial<CheckFormState>) => {
      const stateNext = { ...state, ...stateUpdate };
      stateSet(stateNext);
      checkFormStorage.setState(stateNext);
    },
    [state]
  );

  const hoursSet = React.useCallback(
    (hoursNext: string) => {
      handleSetState({ hours: hoursNext });
    },
    [handleSetState]
  );

  const minutesSet = React.useCallback(
    (minutesNext: string) => {
      handleSetState({ minutes: minutesNext });
    },
    [handleSetState]
  );

  const packagingSet = React.useCallback(
    (packagingNext: string | null) => {
      handleSetState({ packaging: packagingNext });
    },
    [handleSetState]
  );

  const productSet = React.useCallback(
    (productNext: string | null) => {
      handleSetState({ product: productNext });
    },
    [handleSetState]
  );

  const storageSet = React.useCallback(
    (storageNext: string | null) => {
      handleSetState({ storage: storageNext });
    },
    [handleSetState]
  );

  const temperatureSet = React.useCallback(
    (temperatureNext: string) => {
      handleSetState({ temperature: temperatureNext });
    },
    [handleSetState]
  );

  const unitSet = React.useCallback(
    (unitNext: Unit) => {
      handleSetState({ unit: unitNext });
    },
    [handleSetState]
  );

  return (
    <CheckFormContext.Provider
      value={{
        countrySupported,
        getCheckFormSearchParams,
        hours,
        hoursSet,
        minutes,
        minutesSet,
        packaging,
        packagings,
        packagingSet,
        product,
        products,
        productSet,
        storage,
        storages,
        storageSet,
        temperature,
        temperatureSet,
        unit,
        unitSet,
        valid
      }}
    >
      {children}
    </CheckFormContext.Provider>
  );
};

export const useCheckForm = () => React.useContext(CheckFormContext);
